import { isTieredVariant } from '@lub0v/santa-utils';
import { isDotCom } from '@src/site';
import { premiumFeatureAction } from '@src/pages/PremiumPage/featureTier';
import { PremiumOfferView } from '@src/components/PremiumUpsell/PremiumOfferView';
import { trackThemePreview } from '@src/utils/themeAnalytics';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasPremiumFeature, type BoxInfo } from '@lub0v/santa-utils';
import { Button, Stack, Typography } from '@src/components';
import { closeDialog, showDialog, pushDialog, useDialogOpen } from '@src/components/Dialog/Dialog';
import { CrownIcon } from '@src/components/icons/CrownIcon';
import { useStartPremiumPurchase } from '@src/components/PremiumUpsell/premiumInterest';
import { usePremiumOffer } from '@src/pages/PremiumPage/PremiumOfferProvider';
import { GamePreview, ThemePreviewScene } from '@src/pages/NewBoxPage/AppearancePicker';
import { clearDeferredTheme, readDeferredTheme, type DeferredTheme } from '@src/pages/NewBoxPage/deferredTheme';
import { useAppSelector } from '@src/state/hooks';
import { loadSeason } from '@src/theme/registry';
import { useTheme } from '@src/theme/useTheme';
import type { SeasonPackage } from '@src/theme/types';

function ReminderBody({ preference, info }: { preference: DeferredTheme; info: BoxInfo }) {
  const startPremiumPurchase = useStartPremiumPurchase();
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const offer = usePremiumOffer('themes');
  const [previewMode, setPreviewMode] = useState<'light' | 'dark'>('dark');
  const [pkg, setPackage] = useState<SeasonPackage | null>(null);
  useEffect(() => {
    let active = true;
    void loadSeason(preference.theme).then(value => { if (active) setPackage(value); }).catch(() => {});
    return () => { active = false; };
  }, [preference.theme]);
  return <Stack gap={4} wide>
    <Typography as="p" role="body/base">{t('newBox.themeReminder.body', { theme: t(`newBox.appearance.${preference.theme}`) })}</Typography>
    <ThemePreviewScene pkg={pkg} name={info.box.name} picture="santa" logo={null}
      mode={previewMode} onModeChange={setPreviewMode} selectionContext="creation"
      onPreview={() => {
        if (!pkg) return;
        trackThemePreview(preference.theme, 'creation');
        pushDialog({ title: t('newBox.appearance.preview'), fullScreen: true, immersive: true,
          testId: 'theme-full-preview', closeLabel: t('newBox.appearance.close'),
          children: <GamePreview pkg={pkg} name={info.box.name} picture="santa" logo={null}
            mode={previewMode} selectionContext="creation" /> });
      }} />
    <Typography as="p" role="body/base" testId="deferred-theme-benefits">{t(isDotCom() ? 'newBox.themeReminder.benefitsCom' : 'newBox.themeReminder.benefits')}</Typography>
    <Button fullWidth variant={theme === 'dark' ? 'premium-light' : 'premium-dark'} icon={<CrownIcon />}
      testId="deferred-theme-unlock" onClick={() => startPremiumPurchase('deferred_theme_reminder', 'themes', 'creation')}>
      <span className="premium-upsell__button-label premium-upsell__button-label--creation"><span><PremiumOfferView placement="deferred_theme_reminder" feature="themes" context="creation" theme={preference.theme}>{isTieredVariant(offer.variant) ? premiumFeatureAction('themes', i18n.language, offer.symbolPrice) : t('newBox.unlockPremiumPriced', { price: offer.symbolPrice })}</PremiumOfferView></span></span>
    </Button>
    <Button fullWidth variant="secondary" testId="deferred-theme-keep-free" onClick={closeDialog}>{t('newBox.themeReminder.keepFree')}</Button>
  </Stack>;
}

export function DeferredThemeReminder({ info, enabled }: { info: BoxInfo | null; enabled: boolean }) {
  const user = useAppSelector(state => state.session.user);
  const { t } = useTranslation();
  const dialogOpen = useDialogOpen();
  const [interacted, setInteracted] = useState(false);
  const shown = useRef(false);
  const boxKey = info?.box.key;
  const eligible = Boolean(enabled && info?.isAdmin && user && !hasPremiumFeature(info.box, 'boxThemes') && !hasPremiumFeature(user, 'boxThemes'));
  useEffect(() => {
    if (!eligible || dialogOpen || !boxKey || !user || shown.current || !readDeferredTheme(boxKey, user.id)?.ready) return;
    const initialScroll = window.scrollY;
    const scroll = (event: Event) => {
      const top = event.target instanceof Element ? event.target.scrollTop : window.scrollY - initialScroll;
      if (Math.abs(top) >= 80) setInteracted(true);
    };
    const click = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;
      const control = event.target.closest('nav a, nav button, [role="tab"], a[href]');
      if (control instanceof HTMLAnchorElement && !control.pathname.includes(`/box/${boxKey}`)) return;
      if (control) setInteracted(true);
    };
    document.addEventListener('scroll', scroll, true);
    document.addEventListener('click', click);
    return () => {
      document.removeEventListener('scroll', scroll, true);
      document.removeEventListener('click', click);
    };
  }, [eligible, dialogOpen, boxKey, user]);
  useEffect(() => {
    if (!eligible || !interacted || dialogOpen || !boxKey || !user || !info || shown.current) return;
    const timer = window.setTimeout(() => {
      const preference = readDeferredTheme(boxKey, user.id);
      if (!preference?.ready) return;
      shown.current = true;
      clearDeferredTheme(boxKey);
      showDialog({ title: t('newBox.themeReminder.title'), testId: 'deferred-theme-reminder', mobileFullScreen: true,
        children: <ReminderBody preference={preference} info={info} /> });
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [eligible, interacted, dialogOpen, boxKey, user, info, t]);
  return null;
}
